import { incApi } from 'api/inc-api';
import { Paging } from 'components/search/search.hooks';
import { DateTime } from 'luxon';
import { getClientId } from 'util/client-id';
import { generateProvides } from 'util/generateTags';
import { getMerchantId } from 'util/merchant-id';
import {
  LoyaltyAccountSummary,
  LoyaltyHistory,
  LoyaltySpecFormType,
} from './loyalty-domain.type';
import {
  transformLoyaltySpecs,
  transformLoyaltySpecsResponse,
} from './loyalty-domain.utils';

const endpoints = incApi.injectEndpoints({
  overrideExisting: true,
  endpoints: build => ({
    listLoyaltyHistory: build.query<
      { content: LoyaltyHistory[] } & Paging,
      any
    >({
      query: params => ({
        url: `incentivio-loyalty-domain/clients/${getClientId()}/loyaltyaccounts/${
          params.userid
        }/accounthistory`,
        method: 'GET',
        params: {
          ...params,
          merchantid: getMerchantId(),
        },
      }),
      providesTags: generateProvides(
        (_result, _error, params) => params.userid,
        'LoyaltyHistory',
      ),
    }),
    redeemLoyaltyPoints: build.mutation<any, { userId: string; body: any }>({
      query: ({ userId, body }) => {
        return {
          url: `incentivio-loyalty-domain/redeempoints`,
          method: 'POST',
          body: {
            clientId: getClientId(),
            merchantId: getMerchantId(),
            offerCost: body.alignment === 'cost' ? body.points : null,
            offerReward: body.alignment === 'reward' ? body.points : null,
            title: body.reason,
            transactionDate: DateTime.local().toUTC(),
            userId: userId,
          },
        };
      },
      invalidatesTags: (_result, _error, body) => [
        { type: 'LoyaltyHistory', id: body.userId },
        { type: 'AccountLoyaltySummary', id: body.userId },
      ],
    }),
    getLoyaltySpec: build.query<LoyaltySpecFormType, any>({
      query: () =>
        `incentivio-loyalty-domain/clients/${getClientId()}/loyaltyspec/${getMerchantId()}`,
      transformResponse: transformLoyaltySpecsResponse,
      providesTags: ['LoyaltySettings'],
    }),

    updateLoyaltySpecs: build.mutation({
      query: (body: { data: LoyaltySpecFormType; merchantId?: string }) => {
        const finalMerchantId = body.merchantId ?? getMerchantId();

        return {
          url: `incentivio-loyalty-domain/clients/${getClientId()}/loyaltyspec/${finalMerchantId}`,
          method: 'POST',
          body: transformLoyaltySpecs(body.data),
        };
      },
      invalidatesTags: ['LoyaltySettings'],
    }),

    accountLoyaltySummary: build.query<
      LoyaltyAccountSummary,
      { userId: string }
    >({
      query: params => ({
        url: `incentivio-loyalty-domain/clients/${getClientId()}/loyaltyaccounts/${
          params.userId
        }`,
        method: 'GET',
      }),
      providesTags: generateProvides(
        (_result, _error, args) => args.userId,
        'AccountLoyaltySummary',
      ),
    }),
  }),
});

export const {
  useListLoyaltyHistoryQuery,
  useRedeemLoyaltyPointsMutation,
  useGetLoyaltySpecQuery,
  useUpdateLoyaltySpecsMutation,
  useAccountLoyaltySummaryQuery,
} = endpoints;
